import { useCallback, useState } from 'react';

import { useAuth } from '../contexts/AuthContext';
import {
  usePortfolio,
  type PortfolioAccountEdit,
} from '../contexts/PortfolioContext';
import { confirmAction } from '../utils/confirmAction';
import { isPortfolioInvestmentAccount } from '../utils/portfolioAccountCategory';
import { isOwnPortfolioAccount } from '../utils/portfolioOwnership';

export type PortfolioListCategory = 'investment' | 'other';

/**
 * UI + mutation helpers for editing / adding synced portfolio rows.
 * Keeps PortfolioContext focused on fetch/cache; screens share this hook.
 * Global Net-worth shortcuts use PortfolioAccountSheetHost instead.
 */
export function usePortfolioAccountEditor(options?: {
  /** Which bucket this list owns (default: active investments). */
  category?: PortfolioListCategory;
}) {
  const category = options?.category ?? 'investment';
  const categoryIsInvestment = category === 'investment';

  const { user } = useAuth();
  const {
    accounts,
    mutating,
    error,
    latest,
    updateAccount,
    deleteAccount,
    addAccount,
  } = usePortfolio();
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const viewerUserId = user?.id ?? null;

  const editing: PortfolioAccountEdit | null =
    editIndex != null && accounts[editIndex]
      ? {
          label: accounts[editIndex]!.label,
          balance: accounts[editIndex]!.balance,
          expectedReturnPct: accounts[editIndex]!.expectedReturnPct,
          isInvestment: accounts[editIndex]!.isInvestment !== false,
          hiddenFromHousehold: accounts[editIndex]!.hiddenFromHousehold === true,
        }
      : adding
        ? { label: '', balance: 0, isInvestment: categoryIsInvestment }
        : null;

  const openEdit = useCallback(
    (index: number) => {
      const account = accounts[index];
      if (!account || !isOwnPortfolioAccount(account, viewerUserId)) {
        return;
      }
      setAdding(false);
      setEditIndex(index);
    },
    [accounts, viewerUserId],
  );

  const openAdd = useCallback(() => {
    setEditIndex(null);
    setAdding(true);
  }, []);

  const closeEdit = useCallback(() => {
    setEditIndex(null);
    setAdding(false);
  }, []);

  const saveEdit = useCallback(
    async (draft: PortfolioAccountEdit) => {
      const withCategory: PortfolioAccountEdit = {
        ...draft,
        isInvestment: categoryIsInvestment,
        expectedReturnPct: categoryIsInvestment ? draft.expectedReturnPct : undefined,
      };
      if (adding) {
        await addAccount(withCategory);
        setAdding(false);
        return;
      }
      if (editIndex == null) return;
      await updateAccount(editIndex, withCategory);
      setEditIndex(null);
    },
    [adding, addAccount, categoryIsInvestment, editIndex, updateAccount],
  );

  const requestDelete = useCallback(
    async (index: number) => {
      const account = accounts[index];
      if (!account || !isOwnPortfolioAccount(account, viewerUserId)) {
        return;
      }
      const label = account.label?.trim() || 'this account';
      const confirmed = await confirmAction({
        title: 'Delete account?',
        message: `Remove “${label}” from your portfolio? This syncs to all devices.`,
        confirmLabel: 'Delete',
      });
      if (!confirmed) return;
      await deleteAccount(index);
      if (editIndex === index) setEditIndex(null);
    },
    [accounts, deleteAccount, editIndex, viewerUserId],
  );

  const categoryAccounts = accounts
    .map((account, index) => ({
      account,
      index,
      isOwn: isOwnPortfolioAccount(account, viewerUserId),
    }))
    .filter(({ account }) =>
      categoryIsInvestment
        ? isPortfolioInvestmentAccount(account)
        : !isPortfolioInvestmentAccount(account),
    );

  const categoryTotal = categoryAccounts.reduce(
    (sum, row) => sum + row.account.balance,
    0,
  );

  return {
    accounts,
    categoryAccounts,
    totalBalance: categoryTotal,
    mutating,
    error,
    hasSnapshot: latest != null,
    hasAccounts: categoryAccounts.length > 0,
    category,
    categoryIsInvestment,
    editIndex,
    adding,
    editing,
    modalVisible: editIndex != null || adding,
    openEdit,
    openAdd,
    closeEdit,
    saveEdit,
    requestDelete,
    viewerUserId,
  };
}
